// Single source of truth for the ContentIQ wizard look — Direction C
// ("Gallery"), palette derived from the corporate pitch deck
// (Sales/Main Deck/DemandArc_PitchDeck_v11.pptx: dominant navy #131843,
// sky #3C90CE, steel #147694, deck grays; approved by Edward 2026-08-20).
//
// MANAGEABILITY CONTRACT: pages and wizard components take every color,
// font, radius, and shadow from here — never a hex literal in a page file.
// A future app-wide stylesheet/theme feature should absorb this module
// first; until then, changing a value here restyles the whole wizard.
//
// The nav bar (#2c3e50, Arial) is deliberately NOT part of this theme —
// it stays identical across the entire application (Edward's call).

export const FONTS = {
  // Loaded via <link> in public/index.html (Google Fonts). Fallbacks are
  // the app's own stack, so a blocked font host degrades gracefully.
  display: "'Bricolage Grotesque', Arial, sans-serif",
  body: "'Instrument Sans', 'Helvetica Neue', Arial, sans-serif",
};

export const INK = {
  primary: '#131843',   // brand navy — headings + default text
  body: '#3c4850',      // long-form copy
  secondary: '#757575', // deck gray — supporting text
  muted: '#97a0ad',     // hints, timestamps, inactive
};

export const NEUTRAL = {
  pageBg: '#ffffff',
  band: '#f5f6f9',      // section bands / inset rows
  border: '#e3e7ee',
  borderSoft: '#e9edf3',
  chipBg: '#f0f3f6',
  control: '#ccd4dd',   // secondary-button borders
  controlSoft: '#dde3ea',
};

export const BRAND = {
  navy: '#131843',
  navyLine: '#2a3158',  // connector/divider lines on a navy surface
  button: '#243774',    // primary actions
  link: '#2D5996',
  linkHover: '#1F3E68',
  sky: '#3C90CE',       // source links, informational accents
  cyan: '#15D3E5',      // reserved — tiny highlights only, never surfaces
};

// One hue family per deliverable, used consistently everywhere a
// deliverable appears (Home card, GOAL chip, prereq rows, Stage 4 card).
// `key` values match the OUTPUT_DEFS keys in ContentIQStart.js.
export const HUES = {
  matrix:   { main: '#147694', tint: '#f2f7f9', border: '#c5dbe4', deep: '#0e5468', text: '#4a6b7d', dim: '#c3d3da' },
  strategy: { main: '#243774', tint: '#f0f3f9', border: '#c9d5e6', deep: '#131843', text: '#2D5996', dim: '#9db4d4' },
  // Landing pages took the teal family on 2026-10-22 (Edward: recommend) —
  // it was the approval-gate amber, so every landing-page button read as a
  // warning. Teal already marked the landing-page copy questions, and the
  // persona matrix it came from is no longer a deliverable of its own.
  lp:       { main: '#147694', tint: '#f2f7f9', border: '#c5dbe4', deep: '#0e5468', text: '#4a6b7d', dim: '#c3d3da' },
  scripts:  { main: '#6b5ca8', tint: '#f3f2f8', border: '#d6d1e6', deep: '#453a6e', text: '#5c5188', dim: '#cfc8e2' },
};

// Semantic states. The gate family is the ONE deliberately warm tone in
// the system — the approval gate must read as categorically different.
// danger is desaturated to sit with the deck palette; errors should read
// clearly without shouting.
export const STATE = {
  done: { main: '#46795a', tint: '#e7f0ea', deep: '#33543f', text: '#3e644c', border: '#b3ccbc' },
  gate: { main: '#a97b2f', tint: '#f3ecd8', bg: '#faf7ef', border: '#e3d5ab', deep: '#6a5220', text: '#7d6027', strong: '#c2a35a', onNavy: '#e8d9a8' },
  danger: { main: '#b04a44', tint: '#faf1f1', border: '#e7cfcd', deep: '#7c322e', text: '#9c3f3a' },
};

export const RADII = { card: '16px', control: '10px', pill: '20px', tile: '12px', node: '11px', small: '8px' };

export const SHADOW = {
  card: '0 1px 3px rgba(19, 24, 67, 0.05)',
  focus: '0 2px 8px rgba(36, 55, 116, 0.2)',
};

// ── shared style recipes ────────────────────────────────────────────────
// Spread these into inline styles; override per-use as needed.

export const card = {
  background: NEUTRAL.pageBg,
  border: `1px solid ${NEUTRAL.border}`,
  borderRadius: RADII.card,
  boxShadow: SHADOW.card,
};

export const displayFont = (size, weight = 600) => ({
  fontFamily: FONTS.display, fontSize: size, fontWeight: weight, color: INK.primary,
});

export const pill = (fg, bg) => ({
  background: bg, color: fg, borderRadius: RADII.pill,
  padding: '3px 12px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
});

export const buttonPrimary = (bg = BRAND.button) => ({
  background: bg, color: '#ffffff', border: 'none', borderRadius: RADII.control,
  padding: '10px 22px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
  fontFamily: FONTS.body, boxShadow: SHADOW.focus,
});

export const buttonSecondary = {
  background: NEUTRAL.pageBg, color: INK.primary, border: `1.5px solid ${NEUTRAL.control}`,
  borderRadius: RADII.control, padding: '9px 18px', fontSize: '13px', fontWeight: 600,
  cursor: 'pointer', fontFamily: FONTS.body,
};

export const buttonQuiet = {
  ...buttonSecondary, color: INK.secondary, border: `1.5px solid ${NEUTRAL.controlSoft}`,
};

// Compact variants for in-panel controls (table actions, inline downloads) —
// same families as the full-size recipes, sized for dense surfaces.
export const buttonSmall = {
  ...buttonSecondary, border: `1px solid ${NEUTRAL.control}`,
  borderRadius: RADII.small, padding: '4px 12px', fontSize: '12px',
};

export const buttonPrimarySmall = (bg = BRAND.button) => ({
  ...buttonPrimary(bg), padding: '6px 14px', boxShadow: 'none',
});

// One disabled look for every button (spread it over the button's own recipe
// whenever `disabled` is true) — a disabled control must never look live.
export const buttonDisabled = {
  background: NEUTRAL.controlSoft, color: INK.muted, border: 'none', boxShadow: 'none', cursor: 'default',
};

// A navigation or secondary action that reads as a link but is a real,
// keyboard-focusable button (back links, "edit", "add it under Brand").
export const buttonLink = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: BRAND.link,
  fontSize: '12.5px', fontWeight: 600, fontFamily: FONTS.body, textAlign: 'left',
};

export const pageShell = {
  background: NEUTRAL.pageBg, minHeight: 'calc(100vh - 46px)',
  fontFamily: FONTS.body, color: INK.primary,
};
